/**
 * RulebookBrowserPanel — searchable browse of the 29 stable rulebook codes.
 *
 * The same JSON SSOT that drives the validator and the cross-map drives
 * this panel; a stale code or a renamed family fails CI here too. Filter
 * by family and severity, search across code and trigger text, and
 * expand any row to see the full rulebook prose.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React, { useMemo, useState } from "react";
import { PanelSurface, panelTokens } from "@csk/demo-ui";
import rulebookData from "../data/rulebook.json" with { type: "json" };

const ACCENT = "#f59e0b";

interface Rule {
  readonly code: string;
  readonly family: string;
  readonly title: string;
  readonly trigger: string;
  readonly severity: "critical" | "high" | "medium";
}

interface Family {
  readonly id: string;
  readonly label: string;
}

const RULES: readonly Rule[] = rulebookData.rules as readonly Rule[];
const FAMILIES: readonly Family[] = rulebookData.families;

const FAMILY_COLORS: Readonly<Record<string, string>> = {
  SG: "#7080a0",
  DR: "#ec4899",
  E: "#38bdf8",
  P: "#22c55e",
  IC: "#a78bfa",
  CV: "#fbbf24",
};

const SEVERITIES = ["all", "critical", "high", "medium"] as const;
type Severity = (typeof SEVERITIES)[number];

const SEVERITY_COLOR: Readonly<Record<Severity, string>> = {
  all: ACCENT,
  critical: "#ef4444",
  high: "#f59e0b",
  medium: "#38bdf8",
};

export function RulebookBrowserPanel(): React.JSX.Element {
  const [family, setFamily] = useState<string>("all");
  const [severity, setSeverity] = useState<Severity>("all");
  const [query, setQuery] = useState<string>("");
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const toggle = (code: string): void => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(code)) {
        next.delete(code);
      } else {
        next.add(code);
      }
      return next;
    });
  };

  const filtered = useMemo<readonly Rule[]>(() => {
    const needle = query.trim().toLowerCase();
    return RULES.filter((r) => {
      if (family !== "all" && r.family !== family) return false;
      if (severity !== "all" && r.severity !== severity) return false;
      if (needle.length > 0) {
        const hay = `${r.code} ${r.title} ${r.trigger}`.toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      return true;
    });
  }, [family, severity, query]);

  const byFamily = useMemo(() => {
    const map = new Map<string, Rule[]>();
    for (const r of filtered) {
      const list = map.get(r.family) ?? [];
      list.push(r);
      map.set(r.family, list);
    }
    return map;
  }, [filtered]);

  return (
    <PanelSurface.PanelShell
      title="Rulebook Browser"
      subtitle="29 stable codes · 6 families · fail-closed"
      accentColor={ACCENT}
    >
      <PanelSurface.Card>
        <p className="m-0 text-[14.5px] leading-[1.7] text-[var(--ilg-text)]">
          The ILG Validator Rulebook v0 is the registry of every refusal code
          the validator may emit. Codes are stable across versions — downstream
          tooling can name them in tests, dashboards, and SLOs without rewriting
          when a family is reorganized. Every cited code in this demo (apex
          stack, theorems, attacks, seams) resolves here.
        </p>
      </PanelSurface.Card>

      <PanelSurface.Card>
        <PanelSurface.SectionHeading>Filter</PanelSurface.SectionHeading>
        <div className="mt-3.5 flex flex-wrap gap-3 items-center">
          <PanelSurface.TextField
            value={query}
            onChange={setQuery}
            placeholder="code, title, or trigger…"
            ariaLabel="Filter rules"
            label="search"
            mono
            minWidth={240}
          />

          <div
            role="tablist"
            aria-label="Family"
            className="flex flex-wrap gap-1.5"
          >
            <PanelSurface.ChoiceChip
              id="all"
              active={family === "all"}
              color={ACCENT}
              label="All families"
              onSelect={setFamily}
            />
            {FAMILIES.map((f) => {
              const color = FAMILY_COLORS[f.id] ?? panelTokens.muted;
              return (
                <PanelSurface.ChoiceChip
                  key={f.id}
                  id={f.id}
                  active={family === f.id}
                  color={color}
                  label={`${f.id} — ${f.label}`}
                  onSelect={setFamily}
                />
              );
            })}
          </div>

          <div role="tablist" aria-label="Severity" className="flex gap-1.5">
            {SEVERITIES.map((s) => (
              <PanelSurface.ChoiceChip
                key={s}
                id={s}
                active={severity === s}
                color={SEVERITY_COLOR[s]}
                label={s}
                onSelect={setSeverity}
              />
            ))}
          </div>
        </div>
        <div className="mt-3.5 flex gap-2 flex-wrap">
          {PanelSurface.badge(`${filtered.length} / ${RULES.length} shown`, panelTokens.muted)}
        </div>
      </PanelSurface.Card>

      <div className="flex flex-col gap-3.5">
        {FAMILIES.map((f) => {
          const rows = byFamily.get(f.id);
          if (!rows || rows.length === 0) return null;
          const color = FAMILY_COLORS[f.id] ?? panelTokens.muted;
          return (
            <PanelSurface.Card
              key={f.id}
              glowColor={`${color}10`}
              style={{ borderColor: `${color}55` }}
            >
              <div className="flex items-baseline gap-2.5 flex-wrap">
                <span
                  className="font-mono font-extrabold text-[18px]"
                  style={{
                    color,
                    textShadow: `0 0 12px ${color}55`,
                  }}
                >
                  ILG-{f.id}-*
                </span>
                <span className="text-[14px] font-semibold text-[var(--ilg-bright)]">
                  {f.label}
                </span>
                <span className="text-[11px] font-mono text-[var(--ilg-muted)]">
                  {rows.length} code{rows.length === 1 ? "" : "s"}
                </span>
              </div>
              <div className="mt-3 flex flex-col gap-1.5">
                {rows.map((rule) => (
                  <RuleItem
                    key={rule.code}
                    rule={rule}
                    color={color}
                    expanded={expanded.has(rule.code)}
                    onToggle={() => {
                      toggle(rule.code);
                    }}
                  />
                ))}
              </div>
            </PanelSurface.Card>
          );
        })}
        {filtered.length === 0 && (
          <PanelSurface.Card>
            <p className="m-0 text-[13px] text-[var(--ilg-muted)] italic text-center">
              No rules match the current filter.
            </p>
          </PanelSurface.Card>
        )}
      </div>
    </PanelSurface.PanelShell>
  );
}

function RuleItem({
  rule,
  color,
  expanded,
  onToggle,
}: {
  readonly rule: Rule;
  readonly color: string;
  readonly expanded: boolean;
  readonly onToggle: () => void;
}): React.JSX.Element {
  const severityHue =
    rule.severity === "critical"
      ? panelTokens.accentRed
      : rule.severity === "high"
        ? panelTokens.accentAmber
        : panelTokens.accentBlue;
  return (
    <PanelSurface.DisclosureRow
      expanded={expanded}
      onToggle={onToggle}
      color={color}
      leading={
        <span
          className="font-mono font-bold text-[12.5px] tracking-[0.02em] whitespace-nowrap"
          style={{
            color,
          }}
        >
          {rule.code}
        </span>
      }
      primary={rule.title}
      trailing={PanelSurface.badge(rule.severity, severityHue)}
      contentFont="mono"
    >
      <span className="text-[var(--ilg-muted)]">Trigger.</span>{" "}
      {rule.trigger}
    </PanelSurface.DisclosureRow>
  );
}
